"use client";

/**
 * Opens the ContactModal in-place on industry landing pages.
 * Keeps the visitor on `/industry/[vertical]` instead of navigating to `/contact`
 * (which used to send them to the homepage on close).
 * Fires GA `industry_cta_click` before opening the quote funnel.
 */
import { useState, type MouseEvent } from "react";
import dynamic from "next/dynamic";
import {
  trackIndustryCtaClick,
  type IndustryCtaLocation,
} from "@/lib/analytics";

const LazyContactModal = dynamic(
  () => import("@/components/ui/contact-modal").then((m) => m.ContactModal),
  { ssr: false }
);

type IndustryContactCtaProps = {
  // Button or link label (e.g. "Get a Custom School Quote").
  label: string;
  // Industry slug for analytics (e.g. "highschool").
  vertical: string;
  // Where on the page this CTA lives (hero, final_cta, product_card, etc.).
  ctaLocation: IndustryCtaLocation;
  // Visual style for the trigger control.
  variant?: "primary" | "link";
  // Optional extra class names for layout tweaks.
  className?: string;
  // When nested inside a clickable card, stop the click from bubbling to the card.
  stopPropagation?: boolean;
};

export function IndustryContactCta({
  label,
  vertical,
  ctaLocation,
  variant = "primary",
  className = "",
  stopPropagation = false,
}: IndustryContactCtaProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (e: MouseEvent<HTMLButtonElement>) => {
    // Prevent parent card click handlers (e.g. product detail modal) from firing.
    if (stopPropagation) {
      e.stopPropagation();
    }

    trackIndustryCtaClick({ vertical, ctaLocation, label });
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);

  // Shared styles matching the existing industry page CTA look.
  // `cursor-pointer` makes hover clearly show these are clickable (buttons can default to arrow).
  const primaryClassName =
    "inline-flex cursor-pointer items-center justify-center rounded-lg bg-[#8AD4E1] px-8 py-4 text-base font-bold text-black shadow-lg transition hover:bg-[#7BC0CD]";
  // min-h/min-w 48px meets Lighthouse touch-target size (WCAG 2.5.5 / Best Practices).
  const linkClassName =
    "mt-5 inline-flex min-h-12 min-w-12 cursor-pointer items-center py-3 text-sm font-semibold text-[#8AD4E1] hover:underline";

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className={`${variant === "primary" ? primaryClassName : linkClassName} ${className}`}
      >
        {label}
      </button>

      {/* Modal mounts only while open so we keep the industry page URL unchanged. */}
      {isModalOpen && (
        <LazyContactModal
          isOpen={isModalOpen}
          onClose={closeModal}
          analyticsSource="industry_landing"
          analyticsVertical={vertical}
        />
      )}
    </>
  );
}
